class TextScramble {
	constructor(el) {
		this.el = el;
		// Softer character pool — rounder/gentler glyphs that share visual weight
		// with letters, no jagged brackets or carets. Underscores repeat to keep
		// some "settled-looking" frames in the mix.
		this.chars = ':;~<>//||{}[]---$=+';
		this.update = this.update.bind(this);
	}

	setText(newText) {
		const oldText = this.el.textContent;
		const length = Math.max(oldText.length, newText.length);
		const promise = new Promise((resolve) => (this.resolve = resolve));
		this.queue = [];

		for (let i = 0; i < length; i++) {
			const from = oldText[i] || '';
			const to = newText[i] || '';
			const start = Math.floor(Math.random() * 30);
			const end = start + Math.floor(Math.random() * 30);
			this.queue.push({ from, to, start, end });
		}

		cancelAnimationFrame(this.frameRequest);
		this.frame = 0;
		this.update();
		return promise;
	}

	update() {
		let output = '';
		let complete = 0;

		for (let i = 0, n = this.queue.length; i < n; i++) {
			let { from, to, start, end, char } = this.queue[i];

			if (this.frame >= end) {
				complete++;
				output += to;
			} else if (this.frame >= start) {
				// Lower re-roll probability → each random glyph lingers longer,
				// reading as a calmer pulse rather than a flicker.
				if (!char || Math.random() < 0.1) {
					char = this.randomChar();
					this.queue[i].char = char;
				}
				output += `<span class="dud">${char}</span>`;
			} else {
				output += from;
			}
		}

		this.el.innerHTML = output;

		if (complete === this.queue.length) {
			this.resolve();
		} else {
			this.frameRequest = requestAnimationFrame(this.update);
			this.frame++;
		}
	}

	randomChar() {
		return this.chars[Math.floor(Math.random() * this.chars.length)];
	}
}

const markStarted = (el) => {
	if (el.dataset.scrambleStarted) return false;
	el.dataset.scrambleStarted = '1';
	return true;
};

const SCRAMBLE_CHARS = '.:;,~-=+/*•·°____';
const randomChar = () => SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)];

const preScramble = (el) => {
	if (el.dataset.scrambleTarget !== undefined) return;
	const target = el.textContent;
	if (!target) return;
	el.dataset.scrambleTarget = target;
	let output = '';
	for (let i = 0; i < target.length; i++) {
		output += target[i] === ' ' ? ' ' : `<span class="dud">${randomChar()}</span>`;
	}
	el.innerHTML = output;
};

export const runScrambleOnce = (el) => {
	if (!el || !markStarted(el)) return;
	const target = el.dataset.scrambleTarget !== undefined ? el.dataset.scrambleTarget : el.textContent;
	if (!target) return;
	const fx = new TextScramble(el);
	fx.setText(target);
};

const pinWidthToWidestPhrase = (el, phrases) => {
	if (phrases.length <= 1) return;
	const originalHTML = el.innerHTML;
	let maxWidth = 0;

	const measure = () => {
		const w = el.getBoundingClientRect().width;
		if (w > maxWidth) maxWidth = w;
	};

	for (const phrase of phrases) {
		// Settled state (plain characters)
		el.textContent = phrase;
		measure();

		// Mid-scramble state (every char as a dud span) — wider because the
		// parent's letter-spacing doesn't fully apply between inline-block spans
		const scrambled = [...phrase].map(ch => ch === ' ' ? ' ' : `<span class="dud">${ch}</span>`).join('');
		el.innerHTML = scrambled;
		measure();
	}

	el.innerHTML = originalHTML;
	el.style.display = 'inline-block';
	el.style.minWidth = `${Math.ceil(maxWidth)}px`;
	el.style.textAlign = 'center';
};

export const runScrambleCycle = (el, phrases) => {
	if (!el || !markStarted(el)) return;
	const baseText = el.innerText;
	const list = phrases.includes(baseText) ? phrases : [baseText, ...phrases];

	// Lock the rendered width to the widest phrase so layout doesn't reflow on every cycle
	pinWidthToWidestPhrase(el, list);

	const fx = new TextScramble(el);
	let counter = 0;

	const next = () => {
		fx.setText(list[counter]).then(() => {
			setTimeout(next, 2000);
		});
		counter = (counter + 1) % list.length;
	};

	next();
};

export const parseScramblePhrases = (wrapper) => {
	if (!wrapper || !wrapper.dataset.scrambleWords) return null;
	try {
		const phrases = JSON.parse(wrapper.dataset.scrambleWords);
		return Array.isArray(phrases) && phrases.length > 0 ? phrases : null;
	} catch (e) {
		return null;
	}
};

export const tagHeroScrambleDel = (heroEl) => {
	const phrases = parseScramblePhrases(heroEl);
	if (!phrases) return null;
	const del = heroEl.querySelector('del');
	if (!del) return null;
	del.classList.add('scramble-cycle');
	return { del, phrases };
};

const initOneShotObserver = () => {
	const targets = [...document.querySelectorAll('del')].filter(
		(el) => !el.classList.contains('scramble-cycle')
	);
	if (!targets.length) return;

	targets.forEach(preScramble);

	if (!('IntersectionObserver' in window)) {
		targets.forEach(runScrambleOnce);
		return;
	}

	const observer = new IntersectionObserver((entries) => {
		entries.forEach((entry) => {
			if (entry.isIntersecting) {
				runScrambleOnce(entry.target);
				observer.unobserve(entry.target);
			}
		});
	}, { threshold: 0, rootMargin: '0px 0px -10% 0px' });

	targets.forEach((el) => observer.observe(el));
};

const init = () => {
	document.querySelectorAll('section.hero[data-scramble-words]').forEach(tagHeroScrambleDel);
	initOneShotObserver();
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', init);
} else {
	init();
}
